/**
 * InsightConnectorPanel — one-click LLM-powered cross-note relation detector.
 * Use inside PKM Evolution Map tab. Suggests relations the user hasn't already recorded.
 */
import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, Loader2, ArrowRight, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REL_COLORS = {
  supports: '#34C759',
  refines: '#007AFF',
  contradicts: '#FF3B30',
  supersedes: '#FF9500',
};

const InsightConnectorPanel = ({ token, project = '', onAccepted }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState(null);

  const detect = async () => {
    setLoading(true);
    try {
      const url = project ? `${API}/pkm/insights/detect?project=${encodeURIComponent(project)}` : `${API}/pkm/insights/detect`;
      const res = await axios.post(url, {}, { headers });
      setCandidates(res.data.candidates || []);
      setMeta({ note_count: res.data.note_count, project: res.data.scoped_to_project });
      if ((res.data.candidates || []).length === 0) {
        toast.info(res.data.message || 'No new relations detected — the notes don\'t have strong cross-references yet.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Detection failed');
    } finally {
      setLoading(false);
    }
  };

  const accept = async (c) => {
    try {
      await axios.post(`${API}/pkm/insights/accept`, {
        source_note_id: c.source_note_id,
        target_note_id: c.target_note_id,
        relationship_type: c.relationship_type,
        rationale: c.rationale,
      }, { headers });
      toast.success('Relationship saved');
      setCandidates(prev => prev.filter(x => !(x.source_note_id === c.source_note_id && x.target_note_id === c.target_note_id)));
      onAccepted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const reject = (c) => setCandidates(prev => prev.filter(x => !(x.source_note_id === c.source_note_id && x.target_note_id === c.target_note_id)));

  return (
    <Card className="border-[#FF9500]/30 bg-[#FF9500]/5" data-testid="insight-connector">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#FF9500]" />
            <h4 className="text-xs font-semibold text-[#FF9500]">AI Insight Connector</h4>
            {meta && <span className="text-[10px] text-[#9CA3AF]">scanned {meta.note_count} notes{meta.project ? ` in "${meta.project}"` : ''}</span>}
          </div>
          <Button size="sm" className="h-7 text-xs bg-[#FF9500] hover:bg-[#FF9500]/90" onClick={detect} disabled={loading} data-testid="detect-insights-btn">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {candidates.length > 0 ? 'Re-scan' : 'Detect relations'}
          </Button>
        </div>

        {candidates.length === 0 ? (
          <p className="text-[11px] text-[#4B5563]">Click <em>Detect relations</em> and we'll scan your recent notes for connections you haven't recorded yet (supports / refines / contradicts / supersedes).</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {candidates.map(c => (
              <div key={`${c.source_note_id}-${c.target_note_id}`} className="p-2 bg-white rounded border border-[#E5E7EB]" data-testid={`insight-candidate-${c.source_note_id}`}>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span className="text-[#4B5563] font-medium truncate flex-1">{c.source_title}</span>
                  <ArrowRight className="w-3 h-3 text-[#9CA3AF]" />
                  <Badge className="text-[9px]" style={{ backgroundColor: REL_COLORS[c.relationship_type] + '15', color: REL_COLORS[c.relationship_type] }}>{c.relationship_type}</Badge>
                  <ArrowRight className="w-3 h-3 text-[#9CA3AF]" />
                  <span className="text-[#4B5563] font-medium truncate flex-1">{c.target_title}</span>
                  <Badge variant="secondary" className="text-[9px]">{Math.round(c.confidence * 100)}%</Badge>
                </div>
                {c.rationale && <p className="text-[10px] text-[#9CA3AF] italic mb-1.5">"{c.rationale}"</p>}
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-[#34C759] text-[#34C759]" onClick={() => accept(c)} data-testid={`accept-insight-${c.source_note_id}`}>
                    <Check className="w-2.5 h-2.5 mr-1" />Accept
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-[#9CA3AF]" onClick={() => reject(c)}>
                    <X className="w-2.5 h-2.5 mr-1" />Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InsightConnectorPanel;
